import { useState } from 'react';

import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsPencil, BsPlus, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';

import { formatPrice } from '../../../utils/formatters';
import ProductSearchDropdown from '../ProductSearchDropdown/ProductSearchDropdown';

const getBundleItemsTotal = (bundle) => {
	if (!bundle?.items?.length) return null;
	return bundle.items.reduce((sum, it) => {
		const unitPrice = it.priceOverride !== null ? Number(it.priceOverride) : (it.product?.price ?? 0);
		return sum + unitPrice * (it.quantity ?? 1);
	}, 0);
};

const getItemPrice = (dp) => {
	if (dp.priceOverride !== null) return dp.priceOverride;
	if (dp.bundle && dp.bundle.price !== null) return dp.bundle.price;
	if (dp.bundle) return getBundleItemsTotal(dp.bundle);
	if (dp.product && dp.product.price !== null) return dp.product.price;
	return null;
};

const DealProductsSection = ({ deal, allProducts, allBundles, onUpdate }) => {
	const [editing, setEditing] = useState(false);
	const [items, setItems] = useState([]);
	const dealProducts = deal.products ?? [];

	const startEdit = () => {
		setItems(
			dealProducts.map((dp) => ({
				productId: dp.productId ?? '',
				bundleId: dp.bundleId ?? '',
				quantity: dp.quantity ?? 1,
				priceOverride: dp.priceOverride ?? '',
				type: dp.productId ? 'product' : 'bundle',
			})),
		);
		setEditing(true);
	};

	const save = () => {
		const payload = items
			.filter((i) => i.productId || i.bundleId)
			.map((i) =>
				i.productId
					? {
							productId: i.productId,
							quantity: Number(i.quantity) || 1,
							priceOverride: i.priceOverride ? Number(i.priceOverride) : null,
						}
					: {
							bundleId: i.bundleId,
							quantity: Number(i.quantity) || 1,
							priceOverride: i.priceOverride ? Number(i.priceOverride) : null,
						},
			);
		onUpdate(payload);
		setEditing(false);
	};

	const addItem = () => setItems([...items, { productId: '', bundleId: '', quantity: 1, priceOverride: '' }]);

	const productsTotal = dealProducts.reduce((sum, dp) => sum + (getItemPrice(dp) ?? 0) * (dp.quantity ?? 1), 0);
	const selectedIds = new Set(items.map((i) => i.productId || i.bundleId).filter(Boolean));

	if (!editing) {
		return (
			<div className="mt-3 pt-3 border-t border-bgBase-400">
				<div className="flex items-center justify-between mb-2">
					<span className="text-xs-head text-bgBase-800">
						Produkte
						{dealProducts.length > 0 && (
							<span className="text-bgBase-800 font-normal normal-case tracking-normal ml-1">
								· {dealProducts.length}
							</span>
						)}
					</span>
					<div className="flex items-center gap-3">
						{dealProducts.length > 0 && (
							<span className="text-[11px] text-bgBase-800 tabular-nums">
								Summe <span className="font-semibold text-text">{formatPrice(productsTotal)}</span>
							</span>
						)}
						<button
							type="button"
							onClick={startEdit}
							className="text-[11px] text-primary-500 hover:text-primary-600 transition-colors"
						>
							<BsPencil className="inline mr-1 -mt-0.5" />
							Bearbeiten
						</button>
					</div>
				</div>
				{dealProducts.length === 0 ? (
					<p className="text-xs text-bgBase-800">Keine Produkte zugewiesen</p>
				) : (
					<div className="space-y-2">
						{dealProducts.map((dp, i) => {
							const desc = dp.product?.description ?? dp.bundle?.description ?? null;
							const linkTo = dp.productId
								? `/crm/products/${dp.productId}`
								: dp.bundleId
									? `/crm/products/bundles/${dp.bundleId}`
									: null;
							const name = dp.product?.name ?? dp.bundle?.name ?? '–';
							return (
								<div key={dp.id ?? i}>
									<div className="flex justify-between text-sm">
										{linkTo ? (
											<Link
												to={linkTo}
												className="text-primary-500 hover:text-primary-600 hover:underline"
											>
												{name}
											</Link>
										) : (
											<span className="text-text">{name}</span>
										)}
										<span className="text-bgBase-800 tabular-nums">
											{dp.quantity}x{' '}
											{getItemPrice(dp) !== null ? formatPrice(getItemPrice(dp)) : ''}
										</span>
									</div>
									{desc && <p className="text-[11px] text-bgBase-800 mt-0.5 leading-snug">{desc}</p>}
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mt-3 pt-3 border-t border-bgBase-400">
			<span className="text-xs-head text-bgBase-800 mb-2 block">Produkte</span>
			<div className="space-y-2">
				{items.map((item, idx) => (
					<div key={idx} className="flex gap-2 items-center">
						<div className="flex-1 min-w-0">
							<ProductSearchDropdown
								allProducts={allProducts}
								allBundles={allBundles}
								value={item}
								onChange={(val) => {
									const next = [...items];
									next[idx] = { ...next[idx], ...val };
									setItems(next);
								}}
								excludeIds={selectedIds}
							/>
						</div>
						<TextInput
							type="number"
							min={1}
							value={item.quantity}
							onChange={(e) => {
								const next = [...items];
								next[idx] = { ...next[idx], quantity: e.target.value };
								setItems(next);
							}}
							style={{ width: 56, flexBasis: 56, maxWidth: 56 }}
							className="flex-none !px-2 text-center"
							placeholder="Anz."
						/>
						<TextInput
							type="number"
							step="0.01"
							min={0}
							value={item.priceOverride}
							onChange={(e) => {
								const next = [...items];
								next[idx] = { ...next[idx], priceOverride: e.target.value };
								setItems(next);
							}}
							style={{ width: 90, flexBasis: 90, maxWidth: 90 }}
							className="flex-none !px-2 text-right"
							placeholder="Preis €"
						/>
						<IconButton
							icon={BsTrash}
							label="Entfernen"
							tone="danger"
							onClick={() => setItems(items.filter((_, i) => i !== idx))}
						/>
					</div>
				))}
			</div>
			<div className="flex items-center justify-between mt-2">
				<button
					type="button"
					onClick={addItem}
					className="text-[11px] text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1"
				>
					<BsPlus /> Hinzufügen
				</button>
				<div className="flex gap-2">
					<Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)}>
						Abbrechen
					</Button>
					<Button type="button" variant="solid" size="sm" onClick={save}>
						Speichern
					</Button>
				</div>
			</div>
		</div>
	);
};

DealProductsSection.propTypes = {
	deal: PropTypes.object.isRequired,
	allProducts: PropTypes.array.isRequired,
	allBundles: PropTypes.array.isRequired,
	onUpdate: PropTypes.func.isRequired,
};

export default DealProductsSection;
